<template>
  <!-- position-relative anchors the absolutely-positioned copy button. The root
       inherits any class passed by the parent (e.g. mb-3) via Vue attr merging. -->
  <div class="position-relative">
    <pre class="code-block" :class="{ 'code-block-wrap': wrap }"><code>{{ code }}</code></pre>
    <button @click="copy" class="btn btn-sm btn-outline-secondary copy-btn" :class="{ copied }" type="button" :title="copied ? 'Copied!' : 'Copy to clipboard'">
      <span class="fas" :class="copied ? 'fa-check' : 'fa-copy'"></span>
    </button>
  </div>
</template>

<script>
// Reusable copyable code/snippet block: a bordered <pre> with a copy button that
// flashes to a checkmark for 2s. Each instance owns its own copied state, so a
// page can render many without any shared bookkeeping. Used by ApiKeyDocs (curl
// commands / JSON responses, no wrap) and PageUpdates (prompt snippets, wrap).
export default {
  name: 'CodeBlock',
  props: {
    // The exact text shown in the block AND written to the clipboard - one source
    // so the two can never drift.
    code: {
      type: String,
      required: true
    },
    // false (default): white-space: pre - long lines scroll horizontally (code,
    // JSON, shell). true: pre-wrap - long lines wrap (prose snippets).
    wrap: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      copied: false,
      timer: null
    }
  },
  methods: {
    async copy () {
      try {
        await navigator.clipboard.writeText(this.code)
        this.copied = true
        if (this.timer) clearTimeout(this.timer)
        this.timer = setTimeout(() => { this.copied = false }, 2000)
      } catch (e) {
        // Clipboard API unavailable (e.g. non-HTTPS) - the block stays selectable for manual copy.
      }
    }
  },
  beforeUnmount () {
    if (this.timer) clearTimeout(this.timer)
  }
}
</script>

<style scoped>
.code-block {
  background-color: var(--falcon-body-bg);
  border: 1px solid var(--falcon-border-color);
  border-radius: 0.375rem;
  /* Right padding reserves a gutter for the copy button (~2.1rem wide at its
     0.4rem inset), so code that reaches the edge isn't hidden under it. */
  padding: 0.75rem 2.75rem 0.75rem 1rem;
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre;
}
.code-block.code-block-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}
.code-block code {
  color: var(--falcon-emphasis-color);
  background: none;
  padding: 0;
}
/* Pinned top-right of the block, inside the reserved right-padding gutter.
   Opaque background (matches the block) so a very long line scrolled underneath
   is cleanly masked instead of bleeding through the button. */
.copy-btn {
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  padding: 0.1rem 0.4rem;
  line-height: 1;
  /* animate the flash to green on copy and the fade back to normal after */
  transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease;
}
/* Opaque at rest so a long line scrolled under the button is cleanly masked.
   Scoped to :not(:hover) so the outline button's grey hover fill still shows
   (that fill is opaque too, so it keeps masking); excludes .copied so the green
   confirmation wins. */
.copy-btn:not(:hover):not(.copied) {
  background-color: var(--falcon-body-bg);
}
/* Copied confirmation: flash green (like the Copy AI Prompt button). The copy()
   timer clears `copied` after 2s and the transition fades it back to normal. */
.copy-btn.copied {
  background-color: var(--falcon-success);
  border-color: var(--falcon-success);
  color: #fff;
}
</style>
